import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import '../styles/OrderPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cakeTypes = ['Vanilla', 'Chocolate', 'Carrot', 'Lemon', 'Maca', 'Red Velvet', 'Poppy Seed', 'Other'];
const frostingTypes = ['Vanilla', 'Chocolate', 'Cream Cheese (Vegan)', 'Pandan', 'Matcha', 'Lemon', 'Lavender', 'Other'];

const OrderPage = () => {
  const [showCakeOther, setShowCakeOther] = useState(false);
  const [showFrostingOther, setShowFrostingOther] = useState(false);
  const [selectedCakeType, setSelectedCakeType] = useState('');
  const [selectedFrostingType, setSelectedFrostingType] = useState('');
  const [quantity, setQuantity] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const calculateTotal = (qty) => {
    return qty * 5;
  };

  const onSubmit = async (data) => {
    if (quantity < 6) {
      toast.error('Minimum order is 6 cupcakes');
      return;
    }

    if (quantity > 25) {
      toast.error('For orders over 25 cupcakes, please call 773-495-4615 for pricing');
      return;
    }

    setIsSubmitting(true);

    try {
      const orderData = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        cakeType: selectedCakeType,
        cakeTypeOther: showCakeOther ? data.cakeTypeOther : null,
        frostingType: selectedFrostingType,
        frostingTypeOther: showFrostingOther ? data.frostingTypeOther : null,
        quantity: quantity,
        date: data.date,
        notes: data.notes || null
      };

      const response = await axios.post(`${API}/cupcake-order`, orderData);

      if (response.data.status === 'success') {
        setOrderId(response.data.orderId);
        setOrderSuccess(true);
        toast.success('Order placed successfully!');
        reset();
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderSuccess) {
    return (
      <div className="order-success-page">
        <div className="success-container">
          <div className="success-icon">🎉</div>
          <h1>Order Placed Successfully!</h1>
          <p className="order-id">Order ID: {orderId}</p>
          
          <div className="confirmation-message">
            <h2>Check Your Email!</h2>
            <p>We've sent order confirmation and payment instructions to your email.</p>
          </div>

          <div className="payment-instructions">
            <h2>💳 Payment Instructions</h2>
            <div className="payment-total">
              Total Amount: ${calculateTotal(quantity)}
            </div>

            <div className="payment-options">
              <div className="payment-option">
                <h3>🏦 Zelle</h3>
                <p><strong>Send payment to:</strong></p>
                <p className="payment-detail">bettyalper@gmail.com</p>
                <p className="payment-note">Include your name in the note</p>
              </div>

              <div className="payment-option">
                <h3>💸 Venmo</h3>
                <p><strong>Send payment to:</strong></p>
                <p className="payment-detail">@bettybot</p>
                <p className="payment-note">Include your name in the note</p>
              </div>
            </div>
          </div>

          <div className="important-note">
            <p><strong>⏰ Important:</strong> Your order will be confirmed once payment is received. Please complete payment within 24 hours.</p>
          </div>

          <div className="contact-info">
            <p><strong>Questions?</strong></p>
            <p>Text or call Betty at <strong>773-495-4615</strong></p>
            <p>Email: <a href="mailto:bettyalper@gmail.com">bettyalper@gmail.com</a></p>
          </div>

          <Button 
            onClick={() => window.location.href = '/'}
            className="back-home-btn"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-page">
      <div className="order-container">
        <div className="order-header">
          <h1>🧁 Order Cupcakes</h1>
          <p>Delicious vegan & gluten-free cupcakes made with organic ingredients</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="order-form">
          {/* Customer Information */}
          <div className="form-section">
            <h2>Customer Information</h2>
            
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <Input
                id="name"
                {...register('name', { required: 'Name is required' })}
                placeholder="John Doe"
              />
              {errors.name && <span className="error">{errors.name.message}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  placeholder="john@example.com"
                />
                {errors.email && <span className="error">{errors.email.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone *</label>
                <Input
                  id="phone"
                  type="tel"
                  {...register('phone', { required: 'Phone is required' })}
                  placeholder="(555) 123-4567"
                />
                {errors.phone && <span className="error">{errors.phone.message}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">Delivery Address *</label>
              <Textarea
                id="address"
                {...register('address', { required: 'Address is required' })}
                placeholder="123 Main St, Chicago, IL 60649"
                rows={2}
              />
              {errors.address && <span className="error">{errors.address.message}</span>}
            </div>
          </div>

          {/* Cupcake Details */}
          <div className="form-section">
            <h2>Cupcake Details</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cakeType">Cake Type *</label>
                <select
                  id="cakeType"
                  className="form-select"
                  value={selectedCakeType}
                  onChange={(e) => {
                    setSelectedCakeType(e.target.value);
                    setShowCakeOther(e.target.value === 'Other');
                  }}
                  required
                >
                  <option value="">Select cake type</option>
                  {cakeTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {!selectedCakeType && errors.cakeType && <span className="error">Cake type is required</span>}
              </div>

              <div className="form-group">
                <label htmlFor="frostingType">Frosting Type *</label>
                <select
                  id="frostingType"
                  className="form-select"
                  value={selectedFrostingType}
                  onChange={(e) => {
                    setSelectedFrostingType(e.target.value);
                    setShowFrostingOther(e.target.value === 'Other');
                  }}
                  required
                >
                  <option value="">Select frosting type</option>
                  {frostingTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {!selectedFrostingType && errors.frostingType && <span className="error">Frosting type is required</span>}
              </div>
            </div>

            {showCakeOther && (
              <div className="form-group">
                <label htmlFor="cakeTypeOther">Specify Other Cake Type *</label>
                <Input
                  id="cakeTypeOther"
                  {...register('cakeTypeOther', { required: showCakeOther })}
                  placeholder="Enter cake type"
                />
              </div>
            )}

            {showFrostingOther && (
              <div className="form-group">
                <label htmlFor="frostingTypeOther">Specify Other Frosting Type *</label>
                <Input
                  id="frostingTypeOther"
                  {...register('frostingTypeOther', { required: showFrostingOther })}
                  placeholder="Enter frosting type"
                />
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quantity">Quantity * (Min: 6, Max: 25)</label>
                <Input
                  id="quantity"
                  type="number"
                  min="6"
                  max="25"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 6)}
                />
                <p className="form-help">For orders over 25, call 773-495-4615 for pricing</p>
              </div>

              <div className="form-group">
                <label htmlFor="date">Desired Date *</label>
                <Input
                  id="date"
                  type="date"
                  {...register('date', { required: 'Date is required' })}
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.date && <span className="error">{errors.date.message}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Special Instructions (Optional)</label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder="Any special requests or dietary notes..."
                rows={3}
              />
            </div>
          </div>

          {/* Order Summary */}
          <div className="order-summary">
            <h2>Order Summary</h2>
            <div className="summary-row">
              <span>Quantity:</span>
              <span>{quantity} cupcakes</span>
            </div>
            <div className="summary-row">
              <span>Price per cupcake:</span>
              <span>$5.00</span>
            </div>
            <div className="summary-total">
              <span>Total:</span>
              <span>${calculateTotal(quantity)}</span>
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={isSubmitting || !selectedCakeType || !selectedFrostingType}
            className="submit-order-btn"
          >
            {isSubmitting ? 'Placing Order...' : 'Place Order'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default OrderPage;
